import { useState, useCallback, useEffect, FormEvent } from 'react';
import { useLanguage } from '../../i18n/LanguageContext';
import FileDropZone from '../onboarding/FileDropZone';
import MapPicker from '../MapPicker';
import StorageImage from '../StorageImage';
import { stageUpload } from '../../services/uploadService';
import { api } from '../../services/api';
import type { LookupEntryResponse } from '../../types';

type EditTab = 'info' | 'bank' | 'media' | 'activation';

interface GarageMediaItem {
  id: string;
  storageKey: string;
  mediaType: string;
  sortOrder: number;
}

interface GarageData {
  name: string;
  managerName: string;
  phone: string;
  email: string;
  dailyCapacity: number;
  numberOfWorkers: number;
  streetAddress: string;
  city: string;
  area: string;
  cityId?: string | null;
  areaId?: string | null;
  latitude: number | null;
  longitude: number | null;
  logoStorageKey: string | null;
  accountHolder: string | null;
  bankName: string | null;
  iban: string | null;
  /** Spec 081a: "About the garage" (approval-gated through the change request). */
  description?: string | null;
  descriptionAr?: string | null;
  media: GarageMediaItem[];
}

/** Spec 081a: server limit for the garage description, each language. */
const DESCRIPTION_MAX = 1000;

interface EditGaragePanelProps {
  garage: GarageData;
  initialTab?: EditTab;
  token: string;
  onSave: (changes: Record<string, unknown>) => void;
  onCancel: () => void;
}

export default function EditGaragePanel({ garage, initialTab = 'info', token, onSave, onCancel }: EditGaragePanelProps) {
  const { t, lang } = useLanguage();
  // Spec 081a keys are added through the admin Translations page; until then show English.
  const tf = (key: string, fallback: string) => {
    const v = t(key);
    return v && v !== key ? v : fallback;
  };
  const [activeTab, setActiveTab] = useState<EditTab>(initialTab);
  const [infoModified, setInfoModified] = useState(false);
  const [mediaModified, setMediaModified] = useState(false);

  const [form, setForm] = useState({
    name: garage.name || '',
    managerName: garage.managerName || '',
    phone: garage.phone || '',
    dailyCapacity: garage.dailyCapacity || 0,
    streetAddress: garage.streetAddress || '',
    city: garage.city || '',
    area: garage.area || '',
    cityId: garage.cityId || '',
    areaId: garage.areaId || '',
    latitude: garage.latitude,
    longitude: garage.longitude,
    accountHolder: garage.accountHolder || '',
    bankName: garage.bankName || '',
    iban: garage.iban || '',
    description: garage.description || '',
    descriptionAr: garage.descriptionAr || '',
  });
  const [ibanError, setIbanError] = useState<string | null>(null);
  const IBAN_REGEX = /^AE\d{21}$/;

  const updateForm = (field: string, value: unknown) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setInfoModified(true);
  };

  // Lookup-driven city/area selects (mirrors onboarding GarageInfoStep)
  const [cities, setCities] = useState<LookupEntryResponse[]>([]);
  const [areas, setAreas] = useState<LookupEntryResponse[]>([]);

  useEffect(() => {
    api.fetchLookupEntries('CITIES', undefined, token).then(setCities).catch(() => setCities([]));
  }, [token]);

  // Resolve cityId from name once cities have loaded (handles legacy data without cityId)
  useEffect(() => {
    if (!form.cityId && form.city && cities.length > 0) {
      const match = cities.find(c => c.name === form.city);
      if (match) setForm(prev => ({ ...prev, cityId: match.id }));
    }
  }, [cities]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (form.cityId) {
      api.fetchLookupEntries('AREAS', form.cityId, token).then(setAreas).catch(() => setAreas([]));
    } else {
      setAreas([]);
    }
  }, [form.cityId, token]);

  // Resolve areaId from name once areas have loaded
  useEffect(() => {
    if (!form.areaId && form.area && areas.length > 0) {
      const match = areas.find(a => a.name === form.area);
      if (match) setForm(prev => ({ ...prev, areaId: match.id }));
    }
  }, [areas]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCityChange = (newCityId: string) => {
    const city = cities.find(c => c.id === newCityId);
    setForm(prev => ({ ...prev, cityId: newCityId, city: city?.name || '', areaId: '', area: '' }));
    setInfoModified(true);
  };

  const handleAreaChange = (newAreaId: string) => {
    const area = areas.find(a => a.id === newAreaId);
    setForm(prev => ({ ...prev, areaId: newAreaId, area: area?.name || '' }));
    setInfoModified(true);
  };

  const handleMapChange = useCallback((lat: number, lng: number) => {
    setForm(prev => ({ ...prev, latitude: lat, longitude: lng }));
    setInfoModified(true);
  }, []);

  const [newLogo, setNewLogo] = useState<{ mediaId: string; storageKey: string; fileName: string; downloadUrl: string } | null>(null);
  const [newGallery, setNewGallery] = useState<Array<{ mediaId: string; storageKey: string; fileName: string; downloadUrl: string }>>([]);

  // Track existing gallery items and which ones are marked for removal (deduplicate by storageKey)
  const existingGallery = (() => {
    const sorted = (garage.media || []).filter(m => m.mediaType === 'GALLERY_PHOTO').sort((a, b) => a.sortOrder - b.sortOrder);
    const seen = new Set<string>();
    return sorted.filter(m => { if (seen.has(m.storageKey)) return false; seen.add(m.storageKey); return true; });
  })();
  const [removedGalleryKeys, setRemovedGalleryKeys] = useState<string[]>([]);

  const handleRemoveExisting = (storageKey: string) => {
    setRemovedGalleryKeys(prev => [...prev, storageKey]);
    setMediaModified(true);
  };
  const handleUndoRemove = (storageKey: string) => {
    setRemovedGalleryKeys(prev => prev.filter(k => k !== storageKey));
  };

  // Activation tab excluded — it doesn't need approval
  const tabs: { key: EditTab; label: string }[] = [
    { key: 'info', label: t('profile.tab.garageInfo') },
    { key: 'bank', label: t('profile.tab.bank') },
    { key: 'media', label: t('profile.tab.media') },
  ];

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (form.iban && !IBAN_REGEX.test(form.iban)) {
      setIbanError(t('profile.edit.ibanInvalid'));
      setActiveTab('bank');
      return;
    }
    const changes: Record<string, unknown> = {};
    if (infoModified) {
      const garageInfo: Record<string, unknown> = { ...form };
      // Spec 081a: the description goes into the request only when it changed ("" clears it),
      // so an unchanged empty field does not show up as "null → ''" in the admin diff.
      if (form.description === (garage.description || '')) delete garageInfo.description;
      if (form.descriptionAr === (garage.descriptionAr || '')) delete garageInfo.descriptionAr;
      changes.garageInfo = garageInfo;
    }
    if (mediaModified) {
      const hasNewLogo = !!newLogo;
      const hasAdded = newGallery.length > 0;
      const hasRemoved = removedGalleryKeys.length > 0;
      if (hasNewLogo || hasAdded || hasRemoved) {
        const media: Record<string, unknown> = {};
        if (hasNewLogo) media.logoStorageKey = newLogo!.storageKey;
        if (hasAdded) media.galleryAdded = newGallery.map(f => f.storageKey);
        if (hasRemoved) media.galleryRemoved = removedGalleryKeys;
        changes.media = media;
      }
    }
    if (Object.keys(changes).length === 0) { onCancel(); return; }
    onSave(changes);
  };

  return (
    <div className="dialog-backdrop" onClick={onCancel}>
      <div className="edit-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="profile-tabs" style={{ padding: '0 24px', marginBottom: 0 }}>
          {tabs.map((tab) => (
            <button key={tab.key} className={`profile-tab ${activeTab === tab.key ? 'active' : ''}`} onClick={() => setActiveTab(tab.key)} type="button">{tab.label}</button>
          ))}
        </div>

        <form onSubmit={handleSubmit} className="edit-dialog-form">
          <div className="edit-dialog-body">
            <h3 className="edit-panel-title">{t('profile.edit.garageInfo')}</h3>

            {activeTab === 'info' && (
              <div className="edit-panel-content">
                <p className="edit-panel-section">{t('profile.info.basicInfo')}</p>
                <div className="edit-fields-row edit-fields-4">
                  <div className="edit-field"><input type="text" value={form.name} onChange={(e) => updateForm('name', e.target.value)} placeholder={t('profile.edit.garageName.placeholder')} /></div>
                  <div className="edit-field"><input type="text" value={form.managerName} onChange={(e) => updateForm('managerName', e.target.value)} placeholder={t('profile.edit.managerName.placeholder')} /></div>
                  <div className="edit-field"><input type="text" value={form.phone} onChange={(e) => updateForm('phone', e.target.value)} placeholder={t('profile.edit.phone.placeholder')} /></div>
                  <div className="edit-field"><input type="number" value={form.dailyCapacity} onChange={(e) => updateForm('dailyCapacity', Number(e.target.value))} placeholder={t('profile.edit.carCapacity.placeholder')} /></div>
                </div>

                <p className="edit-panel-section" style={{ marginTop: '24px' }}>{tf('profile.info.aboutGarage', 'About the garage')}</p>
                <div className="edit-fields-row edit-fields-2 edit-about-row" data-testid="edit-about-garage">
                  {([
                    { key: 'description', lang: 'en', label: tf('profile.info.description', 'Description (English)') },
                    { key: 'descriptionAr', lang: 'ar', label: tf('profile.info.descriptionAr', 'Description (Arabic)') },
                  ] as const).map((f) => {
                    const value = form[f.key];
                    const atLimit = value.length >= DESCRIPTION_MAX;
                    return (
                      <div className="edit-field edit-field--textarea" key={f.key}>
                        <label htmlFor={`garage-${f.key}`} className="edit-field-label">{f.label}</label>
                        <textarea
                          id={`garage-${f.key}`}
                          dir={f.lang === 'ar' ? 'rtl' : 'ltr'}
                          lang={f.lang}
                          rows={4}
                          maxLength={DESCRIPTION_MAX}
                          value={value}
                          aria-describedby={`garage-${f.key}-count`}
                          data-testid={`garage-${f.key}`}
                          onChange={(e) => updateForm(f.key, e.target.value)}
                        />
                        <span id={`garage-${f.key}-count`} className={`edit-field-counter${atLimit ? ' edit-field-counter--limit' : ''}`}>
                          {value.length}/{DESCRIPTION_MAX}
                        </span>
                      </div>
                    );
                  })}
                </div>

                <p className="edit-panel-section" style={{ marginTop: '24px' }}>{t('profile.edit.location')}</p>
                <div className="edit-fields-row edit-fields-3">
                  <div className="edit-field">
                    <select value={form.cityId} onChange={(e) => handleCityChange(e.target.value)}>
                      <option value="">{t('profile.edit.city.placeholder')}</option>
                      {cities.map((city) => (
                        <option key={city.id} value={city.id}>
                          {lang === 'ar' && city.nameAr ? city.nameAr : city.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="edit-field">
                    {areas.length > 0 ? (
                      <select value={form.areaId} onChange={(e) => handleAreaChange(e.target.value)}>
                        <option value="">{t('profile.edit.area.placeholder')}</option>
                        {areas.map((area) => (
                          <option key={area.id} value={area.id}>
                            {lang === 'ar' && area.nameAr ? area.nameAr : area.name}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type="text"
                        value={form.area}
                        onChange={(e) => updateForm('area', e.target.value)}
                        placeholder={t('profile.edit.area.placeholder')}
                      />
                    )}
                  </div>
                  <div className="edit-field"><input type="text" value={form.streetAddress} onChange={(e) => updateForm('streetAddress', e.target.value)} placeholder={t('profile.edit.streetAddress.placeholder')} /></div>
                </div>

                <p className="edit-panel-section" style={{ marginTop: '16px' }}>{t('profile.edit.mapPicker')}</p>
                <div className="edit-map-container">
                  <MapPicker
                    latitude={form.latitude}
                    longitude={form.longitude}
                    onChange={handleMapChange}
                  />
                </div>
              </div>
            )}

            {activeTab === 'bank' && (
              <div className="edit-panel-content">
                <p className="edit-panel-section">{t('profile.edit.bankDetails')}</p>
                <div className="edit-fields-row edit-fields-3">
                  <div className="edit-field">
                    <input
                      type="text"
                      value={form.accountHolder}
                      onChange={(e) => updateForm('accountHolder', e.target.value)}
                      placeholder={t('settings.accountHolder')}
                      maxLength={200}
                    />
                  </div>
                  <div className="edit-field">
                    <input
                      type="text"
                      value={form.bankName}
                      onChange={(e) => updateForm('bankName', e.target.value)}
                      placeholder={t('settings.bankName')}
                      maxLength={200}
                    />
                  </div>
                  <div className="edit-field">
                    <input
                      type="text"
                      value={form.iban}
                      onChange={(e) => {
                        const v = e.target.value.toUpperCase().replace(/\s+/g, '');
                        updateForm('iban', v);
                        setIbanError(v && !IBAN_REGEX.test(v) ? t('profile.edit.ibanInvalid') : null);
                      }}
                      placeholder={t('settings.iban')}
                      maxLength={34}
                    />
                    {ibanError && (
                      <p style={{ color: '#dc2626', fontSize: '0.75rem', marginTop: '4px' }}>{ibanError}</p>
                    )}
                  </div>
                </div>
                <p style={{ color: '#6b7280', fontSize: '0.75rem', marginTop: '6px' }}>
                  {t('profile.edit.bankDetailsNote')}
                </p>
              </div>
            )}

            {activeTab === 'media' && (
              <div className="edit-panel-content">
                <p className="edit-panel-section">{t('profile.edit.garageLogo')}</p>
                {garage.logoStorageKey && !newLogo && (
                  <div style={{ marginBottom: '1rem' }}>
                    <p style={{ color: '#6b7280', fontSize: '0.8125rem', marginBottom: '0.5rem' }}>{t('profile.edit.currentLogo')}</p>
                    <StorageImage storageKey={garage.logoStorageKey} token={token} alt="Current logo" style={{ width: 96, height: 96, borderRadius: '8px' }} />
                    <p style={{ color: '#9ca3af', fontSize: '0.75rem', marginTop: '0.5rem' }}>{t('profile.edit.replaceLogo')}</p>
                  </div>
                )}
                <FileDropZone
                  mediaType="LOGO" token={token} accept="image/jpeg,image/png" maxFiles={1}
                  label={t('profile.edit.browseUpload')}
                  sublabel={t('profile.edit.acceptedFormats')}
                  files={newLogo ? [newLogo] : []}
                  onUploadComplete={(file) => { setNewLogo(file); setMediaModified(true); }}
                  onRemove={() => setNewLogo(null)}
                  uploadFn={stageUpload}
                />

                <p className="edit-panel-section" style={{ marginTop: '24px' }}>{t('profile.edit.existingGallery')}</p>
                {existingGallery.length > 0 ? (
                  <div className="edit-existing-gallery" data-testid="existing-gallery">
                    {existingGallery.map((item) => {
                      const isRemoved = removedGalleryKeys.includes(item.storageKey);
                      return (
                        <div key={item.id} className={`edit-gallery-item ${isRemoved ? 'removed' : ''}`}>
                          <StorageImage storageKey={item.storageKey} token={token} alt="Gallery" style={{ width: 80, height: 80 }} />
                          {isRemoved ? (
                            <button type="button" className="gallery-undo-btn" onClick={() => handleUndoRemove(item.storageKey)} title={t('common.undoRemove')}>↩</button>
                          ) : (
                            <button type="button" className="gallery-remove-btn" onClick={() => handleRemoveExisting(item.storageKey)} title={t('common.remove')}>&times;</button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p style={{ color: '#9ca3af', fontSize: '0.875rem' }}>{t('profile.edit.noGallery')}</p>
                )}

                <p className="edit-panel-section" style={{ marginTop: '24px' }}>{t('profile.edit.addNewImages')}</p>
                <FileDropZone
                  mediaType="GALLERY_PHOTO" token={token} accept="image/jpeg,image/png" maxFiles={10} multiple
                  label={t('profile.edit.browseUpload')}
                  sublabel={t('profile.edit.acceptedFormats')}
                  files={newGallery}
                  onUploadComplete={(file) => { setNewGallery([...newGallery, file]); setMediaModified(true); }}
                  onRemove={(id) => setNewGallery(newGallery.filter(f => f.mediaId !== id))}
                  uploadFn={stageUpload}
                />
              </div>
            )}
          </div>

          <div className="edit-dialog-footer">
            <button type="button" className="edit-panel-cancel" onClick={onCancel}>{t('profile.edit.cancel')}</button>
            <button type="submit" className="edit-panel-save">{t('profile.edit.save')}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
